import { faChevronDown, faChevronUp, faDownload, faSpinner, faCircleXmark, faUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect, useRef } from 'react'
import { ImageConfig } from './ImageConfig'
import '../viewRequest.css'

import moment from 'moment'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'

const PPQViewRequest = ({ viewingRequest, onUpdate, onClose }) => {
    const [activityLogItems, setActivityLogItems] = useState(null)
    const [requestItems, setRequestItems] = useState(null)
    const [isAccordionOpen, setAccordionOpen] = useState(false)
    const [isDocumentsOpen, setDocumentsOpen] = useState(false)
    const [isSupportingDocumentsOpen, setSupportingDocumentsOpen] = useState(false)
    const [isResending, setIsResending] = useState(false)

    const [supportingDocuments, setSupportingDocuments] = useState([])

    const wrapperRef = useRef(null)
    const [fileList, setFileList] = useState([])
    const [errorMsg, setErrorMsg] = useState(false)

    const { createInfo, auth } = useAuth()
    const axios = useAxiosPrivate()

    useEffect(() => {
        if (viewingRequest?.activity_log) {
            const activityLog =
                typeof viewingRequest.activity_log === 'string'
                    ? JSON.parse(viewingRequest.activity_log)
                    : viewingRequest.activity_log

            setActivityLogItems(activityLog)
        }

        if (viewingRequest?.request_information) {
            const requestInfo =
                typeof viewingRequest.request_information === 'string'
                    ? JSON.parse(viewingRequest.request_information)
                    : viewingRequest.request_information

            setRequestItems(requestInfo)
        }

        if (viewingRequest?.supporting_documents) {
            setSupportingDocuments(viewingRequest.supporting_documents)
        } else {
            setSupportingDocuments([])
        }
    }, [viewingRequest])

    const toggleAccordion = () => {
        setAccordionOpen(!isAccordionOpen)
        setDocumentsOpen(false)
        setSupportingDocumentsOpen(false)
    }

    const toggleDocuments = () => {
        setDocumentsOpen(!isDocumentsOpen)
        setAccordionOpen(false)
        setSupportingDocumentsOpen(false)
    }

    const toggleSupportingDocuments = () => {
        setSupportingDocumentsOpen(!isSupportingDocumentsOpen)
        setAccordionOpen(false)
        setDocumentsOpen(false)
    }

    const getStatusStyle = (status) => {
        const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium'

        if (typeof status !== 'string') {
            return `${baseClasses} bg-gray-200 text-gray-800`
        }

        switch (status.toLowerCase()) {
            case 'form completed - awaiting result':
            case 'no barrier to work':
            case 'no action required':
                return `${baseClasses} bg-green-200 text-green-800 border-green-400`
            case 'awaiting form completion':
            case 'referral needed':
            case 'in progress':
                return `${baseClasses} bg-amber-200 text-amber-800 border-amber-400`
            case 'closed':
            case 'form expired - start date lapsed':
            case 'referral cancelled':
                return `${baseClasses} bg-red-200 text-red-800 border-red-400`
            default:
                if (status.toLowerCase().startsWith('form completed -')) {
                    return `${baseClasses} bg-green-200 text-green-800 border-green-400`
                } else {
                    return `${baseClasses} bg-gray-200 text-gray-800`
                }
        }
    }

    const handleResendPPQ = async () => {
        setIsResending(true)
        try {
            const response = await axios.get('/resend-ppq-form/' + viewingRequest.id)
            createInfo('success', `PEHC form resent successfully`)
            if (onUpdate) {
                onUpdate()
            }
        } catch (error) {
            console.error('Failed to resend PPQ form:', error)
        } finally {
            setIsResending(false)
        }
    }

    const downloadFile = async (fileName, type = 'supporting') => {
        try {
            let downloadUrl

            if (type === 'result') {
                const cleanFileName = fileName.replace('/pdf/oh-result/', '')
                downloadUrl = `${process.env.REACT_APP_KIS}/download-decrypted-file/${cleanFileName}`
            } else {
                downloadUrl = `${process.env.REACT_APP_KIS}/download-supporting-document/${viewingRequest?.service_request_id}/${fileName}`
            }

            window.open(downloadUrl, '_blank').focus()
        } catch (error) {
            console.error('Error downloading the document', error)
        }
    }

    // File upload functions
    const allowedFileTypes = [
        '.doc',
        '.docx',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.xls',
        '.xlsx',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        '.csv',
        'application/pdf',
        '.pdf',
        'image/jpeg',
        '.jpg',
        'image/png',
        '.png',
        'image/tiff',
        '.tiff',
    ]

    const isValidFileType = (file) => {
        const extension = `.${file.name.split('.').pop()}`
        const mimeType = file.type
        return (
            allowedFileTypes.includes(extension) ||
            allowedFileTypes.includes(mimeType)
        )
    }

    const onDragEnter = () => wrapperRef.current.classList.add('dragover')
    const onDragLeave = () => wrapperRef.current.classList.remove('dragover')
    const onDragOver = (e) => {
        e.stopPropagation()
        e.preventDefault()
    }
    const onDrop = (e) => {
        e.preventDefault()
        wrapperRef.current.classList.remove('dragover')
        onFileDrop(e.nativeEvent.dataTransfer.files)
    }

    const onFileUpload = async (e) => {
        e.preventDefault()
        const newFile = e.target.files[0]

        if (newFile && isValidFileType(newFile)) {
            const updatedList = [...fileList, newFile]
            setFileList(updatedList)
        } else {
            console.error('Invalid file type. Please select a valid file.')
        }
    }

    const onFileDrop = async (files) => {
        const newFile = files[0]

        if (newFile && isValidFileType(newFile)) {
            const updatedList = [...fileList, newFile]
            setFileList(updatedList)
        } else {
            createInfo('error', 'Invalid file type')
            return
        }
    }

    const fileRemove = (file) => {
        const updatedList = [...fileList]
        updatedList.splice(fileList.indexOf(file), 1)
        setFileList(updatedList)
    }

    const uploadDocuments = async () => {
        if (fileList.length === 0) {
            createInfo('error', 'Please select files to upload')
            return
        }

        const data = new FormData()

        for (let i = 0; i < fileList.length; i++) {
            data.append('file[]', fileList[i], fileList[i].name)
        }

        // Add current user information
        if (auth?.user?.id) {
            data.append('userId', auth?.user?.id)
        }
        if (auth?.user?.name) {
            data.append('userName', auth?.user?.name)
        }

        try {
            const response = await axios.post(`/upload-supporting-document/${viewingRequest?.service_request_id}`, data,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            )

            if (response.data.result) {
                setFileList([])
                createInfo('success', 'Document Successfully Uploaded')
                window.location.reload()
            }
        } catch (error) {
            console.error('File upload error:', error)
            createInfo('error', 'Failed to upload documents')
        }
    }

    return (
        <div className="space-y-6">
            <div className="border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold text-gray-900">
                        Pre-Placement Questionnaire
                    </h2>
                    <span className={getStatusStyle(viewingRequest?.request_status)}>
                        {viewingRequest?.request_status || 'Unknown'}
                    </span>
                </div>

                {viewingRequest?.request_status === 'Awaiting Form Completion' && (
                    <div className="mt-3 flex items-center justify-between gap-3">
                        <div className="text-sm text-gray-600">
                            PEHC form is awaiting completion.
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={handleResendPPQ}
                                className="inline-flex items-center rounded-md bg-[#02ADAD] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
                                disabled={isResending}
                            >
                                {isResending && (
                                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2 h-4 w-4" />
                                )}
                                Resend PEHC Form
                            </button>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-6 text-sm">
                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Reference</div>
                        <div className="font-mono text-gray-900">{viewingRequest?.request_info_array?.claim_reference || 'N/A'}</div>
                    </div>
                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Type</div>
                        <div className="text-gray-900">Pre-Placement Questionnaire</div>
                    </div>
                </div>

                {viewingRequest?.description && (
                    <div className="text-sm mt-4">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</div>
                        <div className="text-gray-900">{viewingRequest.description}</div>
                    </div>
                )}
            </div>

            <div className="border-b border-gray-200 pb-4">
                <h3 className="font-medium text-gray-900 mb-3">
                    Staff Details
                </h3>
                <div className="grid grid-cols-3 gap-6 text-sm">
                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Name</div>
                        <div className="text-gray-900">
                            {viewingRequest?.request_info_array?.staff_details?.staff_name || 'N/A'}
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Start Date</div>
                        <div className="text-gray-900">
                            {viewingRequest?.request_info_array?.staff_details?.start_date
                                ? moment(viewingRequest.request_info_array.staff_details.start_date).format('DD/MM/YYYY')
                                : 'N/A'}
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Role Applied For</div>
                        <div className="text-gray-900">
                            {viewingRequest?.request_info_array?.staff_details?.role_applied_for || 'N/A'}
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email</div>
                        <div className="text-gray-900 break-all">
                            {viewingRequest?.request_info_array?.staff_details?.staff_email || 'N/A'}
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between">
                    <div
                        className="flex items-center cursor-pointer hover:text-indigo-600 transition-colors"
                        onClick={toggleAccordion}
                    >
                        <h3 className="font-medium text-gray-900">Activity Log</h3>
                        <FontAwesomeIcon
                            icon={isAccordionOpen ? faChevronUp : faChevronDown}
                            className="ml-2 h-4 w-4 text-indigo-900"
                        />
                    </div>
                </div>
                {isAccordionOpen && (
                    <div className="pl-6 mt-6">
                        {activityLogItems &&
                            (Array.isArray(activityLogItems)
                                ? activityLogItems.length > 0
                                : Object.keys(activityLogItems).length > 0) ? (
                            <ol className="relative border-s">
                                {(Array.isArray(activityLogItems)
                                    ? activityLogItems
                                    : [activityLogItems]
                                ).map((log, index) => (
                                    <li className="mb-6 flex items-center" key={index}>
                                        <span className="absolute flex items-center justify-center w-5 h-5 bg-[#02ADAD] rounded-full -start-3 ring-4 ring-white"></span>
                                        <div className="ml-6">
                                            <h3 className="mb-1 text-base font-semibold text-gray-900">
                                                {log.title}
                                            </h3>
                                            <time className="block mb-2 text-sm font-normal leading-none text-gray-400">
                                                {moment(log.timestamp).format('DD/MM/YYYY HH:mm:ss')}
                                            </time>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <ol className="relative border-s ">
                                <li className="mb-10 ms-6">
                                    <span className="absolute flex items-center justify-center w-5 h-5 bg-[#02ADAD] rounded-full -start-3 ring-4 ring-white"></span>
                                    <h3 className="flex items-center mb-1 text-base font-semibold text-gray-900">
                                        No Activity Log Data
                                    </h3>
                                    <time className="block mb-2 text-sm font-normal leading-none text-gray-400">
                                        {moment().format('DD/MM/YYYY HH:mm:ss')}
                                    </time>
                                </li>
                            </ol>
                        )}
                    </div>
                )}
            </div>

            <div className="border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between">
                    <div
                        className="flex items-center cursor-pointer hover:text-indigo-600 transition-colors select-none"
                        onClick={toggleSupportingDocuments}
                    >
                        <h3 className="font-medium text-gray-900">Supporting Documents</h3>
                        <FontAwesomeIcon
                            icon={isSupportingDocumentsOpen ? faChevronUp : faChevronDown}
                            className="ml-2 h-4 w-4 text-indigo-900"
                        />
                    </div>
                </div>
                {isSupportingDocumentsOpen && (
                    <>
                        <div className="mb-4">
                            <div className="flex items-center justify-between mb-4 text-sm">
                                <h3 className="font-semibold">
                                    Upload Documents
                                </h3>
                                <button
                                    onClick={uploadDocuments}
                                    className="rounded-lg border text-xs border-teal-500 bg-teal-500 text-white px-3 py-1 flex items-center hover:bg-transparent hover:text-teal-500"
                                >
                                    <FontAwesomeIcon icon={faUpload} />
                                    <span className="pl-2"> Upload</span>
                                </button>
                            </div>
                            <label
                                htmlFor="file-upload-ppq"
                                className="block"
                                ref={wrapperRef}
                                onDragEnter={onDragEnter}
                                onDragLeave={onDragLeave}
                                onDrop={onDrop}
                                onDragOver={onDragOver}
                            >
                                <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-4 py-6">
                                    <div className="text-center">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth="1.5"
                                            stroke="currentColor"
                                            className="mx-auto h-8 w-8 text-gray-300"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                                            />
                                        </svg>
                                        <div className="mt-2 flex text-xs leading-6 text-gray-600 justify-center">
                                            <div className="relative cursor-pointer rounded-md bg-white font-semibold text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-offset-2 hover:text-indigo-500">
                                                <span>Drag and Drop or click to upload</span>
                                                <input
                                                    id="file-upload-ppq"
                                                    name="file-upload-ppq"
                                                    type="file"
                                                    accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.csv,text/csv,.pdf,application/pdf,image/jpeg,image/png,image/tiff"
                                                    multiple
                                                    className="sr-only"
                                                    onChange={onFileUpload}
                                                />
                                            </div>
                                        </div>
                                        <p className="text-xs leading-5 text-gray-600">
                                            PNG, JPG, CSV, XLS or PDF up to 10MB
                                        </p>
                                        <p className="error-message">{errorMsg}</p>
                                    </div>
                                </div>
                            </label>
                            {fileList.length > 0 && (
                                <div className="mt-4 text-xs">
                                    {fileList.map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between p-2 mb-2 bg-gray-50 rounded"
                                        >
                                            <div className="flex items-center">
                                                <img
                                                    src={
                                                        ImageConfig[item.type.split('/')[1]] ||
                                                        ImageConfig['default']
                                                    }
                                                    alt=""
                                                    className="w-6 h-6"
                                                />
                                                <div className="ml-2">
                                                    <p className="break-words">{item.name}</p>
                                                </div>
                                            </div>
                                            <span
                                                className="hover:text-red-700 cursor-pointer"
                                                onClick={() => fileRemove(item)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faCircleXmark}
                                                    className="text-gray-700 hover:text-red-700 w-4 h-4"
                                                />
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="pl-6 mt-6">
                            {supportingDocuments && supportingDocuments.length > 0 ? (
                                <div className="space-y-3">
                                    {supportingDocuments.map((doc, index) => (
                                        <div key={index} className="flex items-center justify-between border-l-2 border-gray-200 pl-4 py-2">
                                            <div>
                                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Document</div>
                                                <div className="text-sm text-gray-900">{doc.metadata?.original_name || doc?.filename}</div>
                                            </div>
                                            <button
                                                onClick={() => downloadFile(doc.filename)}
                                                className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                                            >
                                                <FontAwesomeIcon icon={faDownload} className="mr-1 h-3 w-3" />
                                                <span className="text-sm">Download</span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500 italic">
                                    No supporting documents available
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {requestItems?.filename && (
                <div className="border-b border-gray-200 pb-4">
                    <div className="flex items-center justify-between">
                        <div
                            className="flex items-center cursor-pointer hover:text-indigo-600 transition-colors"
                            onClick={toggleDocuments}
                        >
                            <h3 className="font-medium text-gray-900">Documents</h3>
                            <FontAwesomeIcon
                                icon={isDocumentsOpen ? faChevronUp : faChevronDown}
                                className="ml-2 h-4 w-4 text-indigo-900"
                            />
                        </div>
                    </div>
                    {isDocumentsOpen && (
                        <div className="pl-6 mt-6">
                            <div className="flex items-center justify-between border-l-2 border-gray-200 pl-4 py-2">
                                <div>
                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Document</div>
                                    <div className="text-sm text-gray-900">PPQ Results Document</div>
                                </div>
                                <button
                                    onClick={() => downloadFile(requestItems.filename, 'result')}
                                    className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                    <FontAwesomeIcon icon={faDownload} className="mr-1 h-3 w-3" />
                                    <span className="text-sm">Download</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default PPQViewRequest